import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { TopProductsCompareRequest, TopProductsCompareResponse, useTopComparedProduct } from '../../../../api/indicators';
import { UserContext } from '../../../../context/UserContext';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatPeriodLabel } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import ComparedPieBarChart, { ComparedPieBarChartData } from '../comparedCharts/ComparedPieBarChart';
import { TableChip } from '../../../../components/EnhancedTableToolbar';

const DEFAULT_LIMIT_PRODUCTS = 10;

const ComparedSalesByProduct = () => {
    const { t } = useTranslation();
    const { locale } = useContext(UserContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);
    const [ params, setParams ] = useState<TopProductsCompareRequest>({
        fields: 'saleValue',
        startDateTime: filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
        endDateTime:filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
        compareStartDateTime: filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate,
        compareEndDateTime:filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate,
        locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
        limit: DEFAULT_LIMIT_PRODUCTS
    });

    const {
        data: dataTopProductCompared,
        isLoading,
        isError,
        refetch: fetchTopProductsCompared
    } = useTopComparedProduct<TopProductsCompareResponse>(params, 'top-products/compared');

    useEffect(() => {
        setParams(p => ({
            ...p,
            startDateTime: filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
            endDateTime:filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
            compareStartDateTime: filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate,
            compareEndDateTime: filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate,
            locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id
        }));
    }, [filters, dateRangeConfig, companyConfig]);

    useEffect(() => {
        fetchTopProductsCompared();
    }, [params, fetchTopProductsCompared]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchTopProductsCompared();
        }
    }, [refreshDate, fetchTopProductsCompared]);

    function transformData(data: TopProductsCompareResponse | undefined): ComparedPieBarChartData[] {
        // no data
        if (!data || (!data?.topProducts.saleValue && !data?.comparedTopProducts.saleValue)) return [];

        // no comparison data
        if (!data.comparedTopProducts) {
            return data.topProducts.saleValue.map(item => ({
                label: item.productName,
                value: item.saleValue ?? 0,
                comparedValue: 0
            }));
        }

        // no current data
        if (!data.topProducts) {
            return data.comparedTopProducts.saleValue.map(item => ({
                label: item.productName,
                value: 0,
                comparedValue: item.saleValue ?? 0
            }));
        }

        // both periods have data
        const currentDataMap = new Map(
            data.topProducts.saleValue.map(item => [item.productName, item.saleValue])
        );
        
        const comparedDataMap = new Map(
            data?.comparedTopProducts?.saleValue?.map(item => [item.productName, item.saleValue])
        );
        
        const allKeys = new Set([
            ...(data.topProducts.saleValue?.map(item => item.productName) || []),
            ...(data.comparedTopProducts.saleValue?.map(item => item.productName) || [])
        ]);
        
        return Array.from(allKeys).map(key => ({
            label: key,
            value: currentDataMap.get(key) ?? 0,
            comparedValue: comparedDataMap.get(key) ?? 0,
        }));
    }
    
    const currentChip: TableChip = useMemo(() => 
            ({
                label: `${formatPeriodLabel(
                    new Date(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                    locale
                  )} - ${formatPeriodLabel(new Date(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                  labelType: 'period'
            }), [filters.periodFrom, filters.periodTo]);
    
            const compareChip: TableChip = useMemo(() => 
            ({
                label: `${formatPeriodLabel(
                    new Date(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                    locale
                  )} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                  labelType: 'periodCompare'
            }), [filters.comparePeriodFrom, filters.comparePeriodTo]);

    return (
        <Box sx={{height: '100%', flex: 1, display: 'flex'}}>
            <ComparedPieBarChart
                title={t('screens.dashboarding.charts.salesByProduct')}
                description={t('screens.dashboarding.charts.includingTax')}
                currency={dataTopProductCompared?.currency}
                data={transformData(dataTopProductCompared)}
                pieLabels={[
                    `${formatPeriodLabel(new Date(filters.periodFrom ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.periodTo ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                    `${formatPeriodLabel(new Date(filters.comparePeriodFrom ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`
                ]}
                locale={locale}
                isLoading={isLoading}
                error={isError}
                height={590}
                reportName={t('screens.dashboarding.charts.salesByProduct')}
                reportLink={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/DailySales.aspx`}
                chips={[currentChip, compareChip]}
            />
        </Box>
    )
}

export default ComparedSalesByProduct;
